import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useRef, useState, } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button, Dialog, DialogClose, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, } from "../../components/base";
import { usePortalRoot } from "../../components/Chat/overlays/portalRootContext";
import { useUiTranslation } from "../../i18n";
export const FormImageLoaderContext = createContext(undefined);
const activeElementIn = (element) => {
    let active = element.ownerDocument.activeElement;
    while (active?.shadowRoot?.activeElement)
        active = active.shadowRoot.activeElement;
    return active;
};
export const DescriptionImage = ({ image }) => {
    const { t } = useUiTranslation();
    const portalRoot = usePortalRoot();
    const loadPreview = useContext(FormImageLoaderContext);
    const [source, setSource] = useState(loadPreview ? undefined : image.url);
    // Without a loader the URL is the only source; a missing one cannot load.
    const [failed, setFailed] = useState(!loadPreview && !image.url);
    const [largeFailed, setLargeFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const retryFocus = useRef(null);
    const closeButton = useRef(null);
    const focusAfterRetry = useCallback((element) => {
        if (!element || !retryFocus.current)
            return;
        if (activeElementIn(element) === retryFocus.current ||
            element.ownerDocument.activeElement === element.ownerDocument.body) {
            element.focus();
            retryFocus.current = element;
        }
        else {
            // Do not interrupt someone who resumed answering during the image load.
            retryFocus.current = null;
        }
    }, []);
    useEffect(() => {
        setFailed(!loadPreview && !image.url);
        if (!loadPreview) {
            setSource(image.url);
            return;
        }
        let active = true;
        let objectUrl;
        setSource(undefined);
        loadPreview({
            id: image.attachmentId,
            uuid: image.attachmentId,
            name: image.altText,
            type: "image/*",
            url: image.url,
            status: "uploaded",
        })
            .then((url) => {
            objectUrl = url;
            if (active)
                setSource(url);
            else if (url.startsWith("blob:"))
                URL.revokeObjectURL(url);
        })
            .catch(() => {
            if (active)
                setFailed(true);
        });
        return () => {
            active = false;
            if (objectUrl?.startsWith("blob:"))
                URL.revokeObjectURL(objectUrl);
        };
    }, [image.attachmentId, image.url, image.altText, loadPreview, attempt]);
    const renderImage = (large = false) => (_jsx("img", { src: source, alt: image.altText, onError: (event) => {
            if (large) {
                setLargeFailed(true);
                return;
            }
            const trigger = event.currentTarget.closest("button");
            if (trigger && activeElementIn(trigger) === trigger)
                retryFocus.current = trigger;
            setFailed(true);
        }, className: large
            ? "block h-auto max-h-[75vh] max-w-full object-contain mx-auto"
            : "block h-auto max-h-64 max-w-full object-contain rounded-md" }, attempt));
    if (failed)
        return (_jsxs("div", { role: "status", className: "rounded-md border p-3 text-xs text-muted-foreground space-y-2", children: [_jsx("p", { children: image.altText }), _jsx("p", { children: t("form.image.failed") }), _jsx(Button, { ref: focusAfterRetry, type: "button", size: "sm", variant: "outline", onClick: (event) => {
                        retryFocus.current = event.currentTarget;
                        setAttempt((value) => value + 1);
                    }, children: t("form.image.retry") })] }));
    if (!source)
        return (_jsx("p", { ref: focusAfterRetry, tabIndex: -1, role: "status", className: "text-xs text-muted-foreground", children: t("form.image.loading") }));
    return (_jsxs(Dialog, { onOpenChange: (open) => open && setLargeFailed(false), children: [_jsx(DialogTrigger, { asChild: true, children: _jsxs("button", { ref: focusAfterRetry, type: "button", className: "block max-w-full text-left rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", "aria-label": t("form.image.view"), children: [renderImage(), _jsx("span", { className: "block mt-1 text-xs text-muted-foreground", children: t("form.image.view") })] }) }), _jsxs(DialogPortal, { container: portalRoot ?? undefined, children: [_jsx(DialogOverlay, {}), _jsxs(DialogPrimitive.Content, { "data-slot": "dialog-content", className: "bg-background text-foreground fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-lg border p-6 shadow-lg outline-none sm:max-w-4xl max-h-[95vh] overflow-y-auto", "aria-describedby": undefined, onOpenAutoFocus: (event) => {
                            event.preventDefault();
                            closeButton.current?.focus();
                        }, onKeyDown: (event) => {
                            if (event.key !== "Tab" ||
                                event.altKey ||
                                event.ctrlKey ||
                                event.metaKey ||
                                !(event.currentTarget.getRootNode() instanceof ShadowRoot))
                                return;
                            // Radix's document.activeElement checks cannot see shadow focus.
                            event.preventDefault();
                            const buttons = Array.from(event.currentTarget.querySelectorAll("button:not([disabled])"));
                            const current = buttons.findIndex((button) => button === activeElementIn(event.currentTarget));
                            const next = current < 0
                                ? event.shiftKey
                                    ? buttons.length - 1
                                    : 0
                                : (current + (event.shiftKey ? -1 : 1) + buttons.length) %
                                    buttons.length;
                            buttons[next].focus();
                        }, children: [_jsx(DialogTitle, { className: "text-sm break-words", children: image.altText }), largeFailed ? (_jsxs("div", { role: "status", className: "space-y-2 text-sm", children: [_jsx("p", { children: t("form.image.failed") }), _jsx(Button, { type: "button", variant: "outline", onClick: () => {
                                            closeButton.current?.focus();
                                            setLargeFailed(false);
                                        }, children: t("form.image.retry") })] })) : (renderImage(true)), _jsx(DialogClose, { asChild: true, children: _jsx(Button, { ref: closeButton, type: "button", variant: "outline", children: t("form.image.close") }) })] })] })] }));
};
export const FieldDescription = ({ helpText, descriptionImage, }) => (_jsxs(_Fragment, { children: [helpText && (_jsx("p", { className: "text-xs text-muted-foreground whitespace-pre-wrap", children: helpText })), descriptionImage && _jsx(DescriptionImage, { image: descriptionImage })] }));
//# sourceMappingURL=FieldDescription.js.map